import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormGroup
} from '@mui/material';
import { 
  VpnKey as KeyIcon,
  Info as InfoIcon,
  GitHub as GitHubIcon,
  Build as BuildIcon,
  Copyright as CopyrightIcon,
  Refresh as RefreshIcon,
  Palette as PaletteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  CloudUpload as UploadIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCustomTheme } from '../contexts/ThemeContext';
import { getBuildInfo, formatBuildDate, formatTimeSince } from '../services/buildInfo';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    currentTheme, 
    themes, 
    baseThemes, 
    customThemes, 
    changeTheme, 
    createCustomTheme, 
    editCustomTheme,
    deleteCustomTheme,
    exportThemes,
    importThemes,
    chartColors 
  } = useCustomTheme();
  const [buildInfo, setBuildInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 커스텀 테마 생성 다이얼로그 상태
  const [customThemeDialog, setCustomThemeDialog] = useState(false);
  const [customThemeName, setCustomThemeName] = useState('');
  const [customPrimaryColor, setCustomPrimaryColor] = useState('#2e7d32');
  const [customSecondaryColor, setCustomSecondaryColor] = useState('#f50057');
  const [dialogTab, setDialogTab] = useState(0);
  const [useCustomChartColors, setUseCustomChartColors] = useState(false);
  const [customChartColors, setCustomChartColors] = useState([
    '#2e7d32', '#66bb6a', '#81c784', '#a5d6a7', '#c8e6c9'
  ]);
  const [editingThemeKey, setEditingThemeKey] = useState(null);
  
  // 테마 가져오기 상태
  const [importMessage, setImportMessage] = useState('');
  const [importSeverity, setImportSeverity] = useState('info');

  const handleChangePassword = () => {
    navigate('/settings/change-password');
  };

  const handleThemeChange = (event) => {
    changeTheme(event.target.value);
  };

  // 커스텀 테마 생성 다이얼로그 열기
  const handleOpenCustomThemeDialog = () => {
    setEditingThemeKey(null);
    setCustomThemeDialog(true);
    setCustomThemeName('');
    setCustomPrimaryColor('#2e7d32');
    setCustomSecondaryColor('#f50057');
    setDialogTab(0);
    setUseCustomChartColors(false);
    setCustomChartColors(['#2e7d32', '#66bb6a', '#81c784', '#a5d6a7', '#c8e6c9']);
  };

  // 커스텀 테마 편집 다이얼로그 열기
  const handleOpenEditThemeDialog = (themeKey) => {
    const theme = customThemes[themeKey];
    if (theme) {
      setEditingThemeKey(themeKey);
      setCustomThemeDialog(true);
      setCustomThemeName(theme.name.replace('커스텀: ', ''));
      setCustomPrimaryColor(theme.palette.primary.main);
      setCustomSecondaryColor(theme.palette.secondary.main);
      setDialogTab(0);
      
      // 차트 색상 설정 확인
      const hasCustomChartColors = theme.chart.colors && theme.chart.colors.length > 0;
      setUseCustomChartColors(hasCustomChartColors);
      setCustomChartColors(hasCustomChartColors ? theme.chart.colors : ['#2e7d32', '#66bb6a', '#81c784', '#a5d6a7', '#c8e6c9']);
    }
  };

  // 커스텀 테마 생성 다이얼로그 닫기
  const handleCloseCustomThemeDialog = () => {
    setCustomThemeDialog(false);
  };

  // 차트 색상 변경
  const handleChartColorChange = (index, color) => {
    const newColors = [...customChartColors];
    newColors[index] = color;
    setCustomChartColors(newColors);
  };

  // 차트 색상 추가
  const handleAddChartColor = () => {
    if (customChartColors.length < 10) {
      setCustomChartColors([...customChartColors, '#2e7d32']);
    }
  };

  // 차트 색상 제거
  const handleRemoveChartColor = (index) => {
    if (customChartColors.length > 3) {
      const newColors = customChartColors.filter((_, i) => i !== index);
      setCustomChartColors(newColors);
    }
  };

  // 주 색상 변경 시 차트 색상 자동 업데이트
  const handlePrimaryColorChange = (color) => {
    setCustomPrimaryColor(color);
    if (!useCustomChartColors) {
      // 자동 생성 모드일 때만 차트 색상 업데이트
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : null;
      };

      const lighten = (color, amount) => {
        const rgb = hexToRgb(color);
        if (!rgb) return color;
        const r = Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * amount));
        const g = Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * amount));
        const b = Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * amount));
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
      };

      const darken = (color, amount) => {
        const rgb = hexToRgb(color);
        if (!rgb) return color;
        const r = Math.max(0, Math.floor(rgb.r * (1 - amount)));
        const g = Math.max(0, Math.floor(rgb.g * (1 - amount)));
        const b = Math.max(0, Math.floor(rgb.b * (1 - amount)));
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
      };

      setCustomChartColors([
        color,
        lighten(color, 0.3),
        lighten(color, 0.5),
        lighten(color, 0.7),
        darken(color, 0.2)
      ]);
    }
  };

  // 커스텀 테마 생성/편집
  const handleCreateOrEditCustomTheme = () => {
    if (customThemeName.trim()) {
      let chartColorsToUse = null;
      
      if (useCustomChartColors) {
        chartColorsToUse = {
          primary: customChartColors[0] || customPrimaryColor,
          secondary: customChartColors[1] || customChartColors[0],
          accent: customChartColors[2] || customChartColors[0],
          colors: customChartColors
        };
      }
      
      if (editingThemeKey) {
        // 편집 모드
        editCustomTheme(editingThemeKey, customThemeName.trim(), customPrimaryColor, customSecondaryColor, chartColorsToUse);
      } else {
        // 생성 모드
        createCustomTheme(customThemeName.trim(), customPrimaryColor, customSecondaryColor, chartColorsToUse);
      }
      
      handleCloseCustomThemeDialog();
    }
  };

  // 커스텀 테마 삭제
  const handleDeleteCustomTheme = (themeKey) => {
    if (window.confirm('이 커스텀 테마를 삭제하시겠습니까?')) {
      deleteCustomTheme(themeKey);
    }
  };

  // 테마 내보내기
  const handleExportThemes = () => {
    try {
      exportThemes();
      setImportMessage('테마가 성공적으로 내보내졌습니다.');
      setImportSeverity('success');
      setTimeout(() => setImportMessage(''), 3000);
    } catch (error) {
      setImportMessage(`내보내기 실패: ${error.message}`);
      setImportSeverity('error');
      setTimeout(() => setImportMessage(''), 5000);
    }
  };

  // 테마 가져오기
  const handleImportThemes = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setImportMessage('JSON 파일만 업로드 가능합니다.');
      setImportSeverity('error');
      setTimeout(() => setImportMessage(''), 5000);
      return;
    }

    importThemes(file)
      .then((result) => {
        if (result.success) {
          setImportMessage(result.message);
          setImportSeverity('success');
        } else {
          setImportMessage(result.message);
          setImportSeverity('warning');
        }
        setTimeout(() => setImportMessage(''), 5000);
      })
      .catch((error) => {
        setImportMessage(error.message);
        setImportSeverity('error');
        setTimeout(() => setImportMessage(''), 5000);
      })
      .finally(() => {
        // 파일 입력 초기화
        event.target.value = '';
      });
  };

  const loadBuildInfo = async () => {
    setLoading(true);
    try {
      const info = await getBuildInfo();
      setBuildInfo(info);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBuildInfo();
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        설정
      </Typography>
      
      <Grid container spacing={3}>
        {/* 계정 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                계정 설정
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  사용자명
                </Typography>
                <Typography variant="body1">
                  {user?.username}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  계정 유형
                </Typography>
                <Typography variant="body1">
                  {user?.is_admin ? '관리자' : '사용자'}
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<KeyIcon />}
                onClick={handleChangePassword}
                sx={{ mt: 2, mr: 1 }}
              >
                비밀번호 변경
              </Button>
              <Button
                variant="outlined"
                startIcon={<StorageIcon />}
                onClick={() => navigate('/settings/storage')}
                sx={{ mt: 2 }}
              >
                스토리지 설정
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 테마 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                테마 설정
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="theme-select-label">색상 테마</InputLabel>
                  <Select
                    labelId="theme-select-label"
                    value={currentTheme}
                    label="색상 테마"
                    onChange={handleThemeChange}
                  >
                    {/* 기본 테마들 */}
                    {Object.entries(baseThemes).map(([key, theme]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: theme.palette.primary.main,
                              borderRadius: '50%',
                              border: '1px solid #ddd'
                            }}
                          />
                          {theme.name}
                        </Box>
                      </MenuItem>
                    ))}
                    
                    {/* 커스텀 테마들 */}
                    {Object.entries(customThemes).length > 0 && [
                      <Divider key="divider" />,
                      ...Object.entries(customThemes).map(([key, theme]) => (
                        <MenuItem key={key} value={key}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '50%',
                                border: '1px solid #ddd'
                              }}
                            />
                            <Box sx={{ flexGrow: 1 }}>{theme.name}</Box>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditThemeDialog(key);
                              }}
                              sx={{ color: 'primary.main' }}
                              title="테마 편집"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCustomTheme(key);
                              }}
                              sx={{ color: 'error.main' }}
                              title="테마 삭제"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </MenuItem>
                      ))
                    ]}
                  </Select>
                </FormControl>
                
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCustomThemeDialog}
                  sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                >
                  커스텀 테마
                </Button>
              </Box>

              {/* 테마 내보내기/가져오기 섹션 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  테마 백업 및 복원
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ExportIcon />}
                    onClick={handleExportThemes}
                  >
                    내보내기
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ImportIcon />}
                    component="label"
                  >
                    가져오기
                    <input
                      type="file"
                      hidden
                      accept=".json"
                      onChange={handleImportThemes}
                    />
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  커스텀 테마를 JSON 파일로 내보내거나 가져올 수 있습니다.
                </Typography>
              </Box>

              {/* 가져오기 결과 메시지 */}
              {importMessage && (
                <Alert severity={importSeverity} sx={{ mb: 2 }}>
                  {importMessage}
                </Alert>
              )}

              {/* 현재 테마 색상 미리보기 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  현재 테마 차트 색상
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))', 
                  gap: 1, 
                  p: 1.5, 
                  bgcolor: 'background.default', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  {chartColors.colors.map((color, index) => (
                    <Box key={index} sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 35,
                          backgroundColor: color,
                          borderRadius: 1,
                          border: '2px solid #fff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 0.5
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'white', 
                            fontWeight: 'bold',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                            fontSize: '0.7rem'
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                        {color.slice(1).toUpperCase()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary">
                선택한 테마는 자동으로 저장되며, 대시보드의 차트 색상도 함께 변경됩니다.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 빌드 정보 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  빌드 정보
                </Typography>
                <Tooltip title="빌드 정보 새로고침">
                  <IconButton size="small" onClick={loadBuildInfo} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {loading ? (
                <Typography variant="body2" color="text.secondary">
                  빌드 정보를 불러오는 중...
                </Typography>
              ) : buildInfo ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      버전
                    </Typography>
                    <Typography variant="body1">
                      v{buildInfo.version}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      빌드 날짜
                    </Typography>
                    <Typography variant="body1">
                      {formatBuildDate(buildInfo.buildDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({formatTimeSince(buildInfo.buildTimestamp)})
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Git 정보
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Chip 
                        icon={<GitHubIcon />}
                        label={`${buildInfo.gitBranch}@${buildInfo.gitHash}`}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      빌드 환경
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Chip 
                        icon={<BuildIcon />}
                        label={`Node ${buildInfo.nodeVersion}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip 
                        label={buildInfo.platform}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  빌드 정보를 불러올 수 없습니다.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* 시스템 정보 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                시스템 정보
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    시스템 이름
                  </Typography>
                  <Typography variant="body1">
                    GPU KVM API Server
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    설명
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    KVM 환경에서 GPU 가상머신을 관리하는 웹 기반 API 서버
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    주요 기능
                  </Typography>
                  <Stack direction="column" spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      • GPU 패스스루 가상머신 관리
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      • OS 이미지 및 디스크 볼륨 관리
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      • Cloud-init 자동 설정
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      • 웹 기반 대시보드
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 저작권 정보 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                저작권 정보
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CopyrightIcon color="primary" />
                  <Typography variant="body1">
                    {buildInfo?.copyright ? 
                      `${buildInfo.copyright.year} ${buildInfo.copyright.holder}` : 
                      `${new Date().getFullYear()} ypkim`
                    }
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary">
                  이 소프트웨어는 MIT 라이선스 하에 제공됩니다.
                </Typography>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                  GPU KVM API Server는 가상화 환경에서 GPU 리소스를 
                  효율적으로 관리하기 위해 개발되었습니다.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 커스텀 테마 생성 다이얼로그 */}
      <Dialog 
        open={customThemeDialog} 
        onClose={handleCloseCustomThemeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaletteIcon />
            {editingThemeKey ? '커스텀 테마 편집' : '커스텀 테마 생성'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={dialogTab} onChange={(e, newValue) => setDialogTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label="기본 설정" />
            <Tab label="차트 색상" />
          </Tabs>

          {/* 기본 설정 탭 */}
          {dialogTab === 0 && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="테마 이름"
                value={customThemeName}
                onChange={(e) => setCustomThemeName(e.target.value)}
                fullWidth
                placeholder="예: 내 테마"
              />
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                주 색상 (Primary Color)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  type="color"
                  value={customPrimaryColor}
                  onChange={(e) => handlePrimaryColorChange(e.target.value)}
                  sx={{ width: 80 }}
                />
                <TextField
                  value={customPrimaryColor}
                  onChange={(e) => handlePrimaryColorChange(e.target.value)}
                  placeholder="#2e7d32"
                  fullWidth
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                UI 요소와 차트의 기본 색상으로 사용됩니다.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                보조 색상 (Secondary Color)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  type="color"
                  value={customSecondaryColor}
                  onChange={(e) => setCustomSecondaryColor(e.target.value)}
                  sx={{ width: 80 }}
                />
                <TextField
                  value={customSecondaryColor}
                  onChange={(e) => setCustomSecondaryColor(e.target.value)}
                  placeholder="#f50057"
                  fullWidth
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                액센트 색상으로 사용됩니다.
              </Typography>
            </Box>

            {/* 색상 미리보기 */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                미리보기
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: customPrimaryColor,
                    borderRadius: 1,
                    border: '1px solid #ddd'
                  }}
                />
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: customSecondaryColor,
                    borderRadius: 1,
                    border: '1px solid #ddd'
                  }}
                />
                {/* 자동 생성될 색상들 미리보기 */}
                {customPrimaryColor && (
                  <>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: `${customPrimaryColor}80`,
                        borderRadius: 1,
                        border: '1px solid #ddd'
                      }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: `${customPrimaryColor}60`,
                        borderRadius: 1,
                        border: '1px solid #ddd'
                      }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: `${customPrimaryColor}40`,
                        borderRadius: 1,
                        border: '1px solid #ddd'
                      }}
                    />
                  </>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                주 색상을 기반으로 차트용 색상 팔레트가 자동 생성됩니다.
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              생성된 커스텀 테마는 브라우저에 저장되며, 선택 목록에서 삭제할 수 있습니다.
            </Alert>
            </Stack>
          )}

          {/* 차트 색상 탭 */}
          {dialogTab === 1 && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useCustomChartColors}
                      onChange={(e) => setUseCustomChartColors(e.target.checked)}
                    />
                  }
                  label="차트 색상 직접 설정"
                />
                <Typography variant="caption" color="text.secondary">
                  비활성화 시 주 색상을 기반으로 자동 생성됩니다.
                </Typography>
              </FormGroup>

              {useCustomChartColors ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    차트 색상 팔레트 (최소 3개, 최대 10개)
                  </Typography>
                  <Stack spacing={2}>
                    {customChartColors.map((color, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ minWidth: 60 }}>
                          색상 {index + 1}
                        </Typography>
                        <TextField
                          type="color"
                          value={color}
                          onChange={(e) => handleChartColorChange(index, e.target.value)}
                          sx={{ width: 80 }}
                        />
                        <TextField
                          value={color}
                          onChange={(e) => handleChartColorChange(index, e.target.value)}
                          placeholder="#2e7d32"
                          fullWidth
                        />
                        {customChartColors.length > 3 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveChartColor(index)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    
                    {customChartColors.length < 10 && (
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddChartColor}
                        variant="outlined"
                        size="small"
                      >
                        색상 추가
                      </Button>
                    )}
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    자동 생성 미리보기
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))', 
                    gap: 1.5, 
                    p: 2, 
                    bgcolor: 'background.default', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    {customChartColors.map((color, index) => (
                      <Box key={index} sx={{ textAlign: 'center' }}>
                        <Box
                          sx={{
                            width: '100%',
                            height: 45,
                            backgroundColor: color,
                            borderRadius: 1,
                            border: '2px solid #fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 0.5,
                            position: 'relative'
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'white', 
                              fontWeight: 'bold',
                              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                              fontSize: '0.75rem'
                            }}
                          >
                            {index + 1}
                          </Typography>
                          
                          {/* 기본 차트 용도 라벨 */}
                          {index < 3 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -6,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: '0.55rem',
                                color: 'text.secondary',
                                bgcolor: 'background.paper',
                                px: 0.3,
                                borderRadius: 0.3,
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              {['CPU', '메모리', '디스크'][index]}
                            </Box>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {color.slice(1).toUpperCase()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    주 색상을 변경하면 이 색상들이 자동으로 업데이트됩니다.
                  </Typography>
                </Box>
              )}

              {/* 차트 미리보기 */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  차트 미리보기
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
                  gap: 1.5, 
                  p: 2, 
                  bgcolor: 'background.default', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  {customChartColors.map((color, index) => (
                    <Box key={index} sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 50,
                          backgroundColor: color,
                          borderRadius: 1,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid #fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          position: 'relative'
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'white', 
                            fontWeight: 'bold', 
                            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                            fontSize: '0.8rem'
                          }}
                        >
                          {index + 1}
                        </Typography>
                        
                        {/* 기본 차트 용도 라벨 */}
                        {index < 3 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: -8,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '0.6rem',
                              color: 'text.secondary',
                              bgcolor: 'background.paper',
                              px: 0.5,
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            {['CPU', '메모리', '디스크'][index]}
                          </Box>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {color.toUpperCase()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  총 {customChartColors.length}개 색상 • 숫자는 차트에서 사용되는 순서입니다
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomThemeDialog}>
            취소
          </Button>
          <Button 
            onClick={handleCreateOrEditCustomTheme}
            variant="contained"
            disabled={!customThemeName.trim()}
          >
            {editingThemeKey ? '수정' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage; 